import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ContactRequest } from '../api/BackofficeApi';
import { contactAPI } from '../services/api';

const LABELS = {
  title: 'צור קשר',
  subtitle: 'מלא את הפרטים למטה ונחזור אליך בהקדם.',
  name: 'שם מלא',
  companyName: 'שם החברה',
  email: 'דוא"ל',
  phone: 'טלפון',
  message: 'הודעה',
  messagePlaceholder: 'במה נוכל לעזור לך?',
  send: 'שלח הודעה',
  sending: 'שולח...',
  success: 'תודה! ההודעה נשלחה בהצלחה.',
  errorDefault: 'השליחה נכשלה. אנא נסה שוב.',
  errors: {
    name: 'שדה חובה',
    companyName: 'שם החברה חובה',
    email: 'דוא"ל חובה',
    emailInvalid: 'נא להזין כתובת דוא"ל תקינה',
    phone: 'טלפון חובה',
  },
};

export default function Contact() {
  const [formData, setFormData] = useState<ContactRequest>({
    name: '',
    companyName: '',
    email: '',
    phone: '',
    content: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof ContactRequest, string>>>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof ContactRequest, string>> = {};
    if (!formData.name.trim()) newErrors.name = LABELS.errors.name;
    if (!formData.companyName.trim()) newErrors.companyName = LABELS.errors.companyName;
    if (!formData.email.trim()) newErrors.email = LABELS.errors.email;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = LABELS.errors.emailInvalid;
    }
    if (!formData.phone.trim()) newErrors.phone = LABELS.errors.phone;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof ContactRequest) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus('submitting');
    setErrorMessage('');

    try {
      await contactAPI.submitContact({
        ...formData,
        content: formData.content || undefined,
      });
      setStatus('success');
      setFormData({ name: '', companyName: '', email: '', phone: '', content: '' });
    } catch (err: any) {
      setStatus('error');
      setErrorMessage(err?.message || err?.data?.message || LABELS.errorDefault);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
        px: 2,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
        <Box
          component="a"
          href="/"
          sx={{ textDecoration: 'none', display: 'block' }}
        >
          <Box
            component="img"
            src="/logo.png"
            alt="EnerJet.co.il"
            sx={{ height: 120, width: 'auto', display: 'block' }}
          />
        </Box>
        <Typography
          variant="body1"
          sx={{ mt: 1, color: 'text.secondary', fontSize: '1.5rem' }}
        >
          תכניות סולאריות אוטומטיות
        </Typography>
      </Box>
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            border: '1px solid',
            borderColor: 'rgba(21, 128, 61, 0.2)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(21, 128, 61, 0.08)',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            fontWeight={700}
            sx={{ color: 'primary.main' }}
          >
            {LABELS.title}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3, fontSize: '1.05rem' }}>
            {LABELS.subtitle}
          </Typography>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={LABELS.name}
              value={formData.name}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              margin="normal"
              required
              inputProps={{ dir: 'rtl' }}
            />
            <TextField
              fullWidth
              label={LABELS.companyName}
              value={formData.companyName}
              onChange={handleChange('companyName')}
              error={!!errors.companyName}
              helperText={errors.companyName}
              margin="normal"
              required
              inputProps={{ dir: 'rtl' }}
            />
            <TextField
              fullWidth
              label={LABELS.email}
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              margin="normal"
              required
              inputProps={{ dir: 'ltr' }}
            />
            <TextField
              fullWidth
              label={LABELS.phone}
              value={formData.phone}
              onChange={handleChange('phone')}
              error={!!errors.phone}
              helperText={errors.phone}
              margin="normal"
              required
              inputProps={{ dir: 'ltr' }}
            />
            <TextField
              fullWidth
              label={LABELS.message}
              value={formData.content || ''}
              onChange={handleChange('content')}
              margin="normal"
              multiline
              rows={4}
              placeholder={LABELS.messagePlaceholder}
              inputProps={{ dir: 'rtl' }}
            />

            {status === 'success' && (
              <Alert severity="success" sx={{ mt: 2 }} dir="rtl">
                {LABELS.success}
              </Alert>
            )}
            {status === 'error' && (
              <Alert severity="error" sx={{ mt: 2 }} dir="rtl">
                {errorMessage}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={status === 'submitting'}
              sx={{ mt: 3 }}
            >
              {status === 'submitting' ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1 }} color="inherit" />
                  {LABELS.sending}
                </>
              ) : (
                LABELS.send
              )}
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}
